import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import aggregateSpectrograms from '@client/utils/aggregateSpectrograms';
import calculateDli from '@client/utils/calculateDli';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import {
    CircadianEvent,
    DARK_LIGHT_RECIPE_ID,
    LinkedZoneDoc,
    PauseData,
    ProjectStatus,
    RecipeSection,
    SpectrogramMapItem,
    SpectrogramPoint
} from '@ledium/types';

import DashboardProjectView from './DashboardProjectView';
import { SectionDliInfo } from './DliInfoPopper';

interface SpectrumMixLightChannel {
    id: string;
    name: string;
    resolution: number;
    spectrogram: SpectrogramPoint[];
    spectrogramRanges: { pfd: { sum: number }; irradiance: { sum: number } };
    intensityPercent: number;
    intensityRanges: { pfd: { sum: number; uv: number; blue: number; green: number; red: number; fr: number; ir: number }; irradiance: { sum: number } };
}

interface SpectrumMixData {
    id: string;
    name: string;
    lightRecipe: { name: string };
    lightChannels: SpectrumMixLightChannel[];
}

interface CircadianEventRuntime extends CircadianEvent {
    spectrumMix: SpectrumMixData;
}

interface SectionRuntime extends Omit<RecipeSection, 'circadianEvents'> {
    lightChannels: Record<string, Pick<SpectrumMixLightChannel, 'id' | 'name' | 'spectrogram' | 'spectrogramRanges'>>;
    maxWaveLengthIntensity: number;
    maxIrradianceIntensity: number;
    circadianEvents: CircadianEventRuntime[];
}

export interface DashboardProjectItem {
    id: string;
    name: string;
    description?: string;
    zoneId?: string;
    group?: { name: string };
    running: boolean;
    startTime: number | null;
    pauseData: PauseData | null;
    spectrogramMap: SpectrogramMapItem[];
    errorCode?: string;
    recipe?: { sectionList: RecipeSection[] } | null;
    linkCount?: number;
    linkErrorCount?: number;
    mirrorLinkErrorCount?: number;
    linkAllDisabled?: boolean;
    independentLink?: {
        enabled: boolean;
        disabledReason: 'user' | 'mapping_error' | null;
        sourceZoneName: string;
        sourceProjectName: string;
    };
}

export interface ProjectSocketData {
    projectId: string;
    status: ProjectStatus;
}

interface DashboardProjectProps {
    project: DashboardProjectItem;
    projectData: ProjectSocketData | null | undefined;
    refreshProjects: () => void;
    handleStopProject: (projectId: string) => void;
}

async function fetchSpectrumMix(id: string, cache: Record<string, SpectrumMixData>): Promise<SpectrumMixData | undefined> {
    if (!cache[id]) {
        const data = await lediumApi.get<SpectrumMixData>(`spectrumMixes/withLightChannels/${id}`);
        if (data) {
            cache[id] = data;
        }
    }
    return cache[id];
}

function buildSectionPfdPoints(circadianEvents: CircadianEventRuntime[]): { time: number; value: number }[] {
    let nextTurnOff1BitPfd = 0;
    const pfdPoints: { time: number; value: number }[] = [];

    [...circadianEvents]
        .sort((a, b) => (+a.hour + a.minute / 60 > +b.hour + b.minute / 60 ? 1 : -1))
        .forEach((event) => {
            const time = event.hour + event.minute / 60;
            const [fadePfd, turnPfd] = event.spectrumMix.lightChannels.reduce(
                (a: [number, number], lightChannel) => {
                    if (lightChannel.resolution === 1) {
                        a[1] += (lightChannel.intensityRanges.pfd.sum / 100) * lightChannel.intensityPercent;
                    } else {
                        a[0] += (lightChannel.intensityRanges.pfd.sum / 100) * lightChannel.intensityPercent;
                    }
                    return a;
                },
                [0, 0]
            );

            pfdPoints.push({ time, value: nextTurnOff1BitPfd + fadePfd });

            if (turnPfd) {
                nextTurnOff1BitPfd = turnPfd;
                pfdPoints.push({ time: time + 1 / 3600, value: fadePfd + turnPfd });
            } else {
                nextTurnOff1BitPfd = 0;
            }
        });

    if (nextTurnOff1BitPfd) {
        pfdPoints.push({ time: 23 + 55 / 60 - 1 / 3600, value: nextTurnOff1BitPfd });
    }

    return pfdPoints;
}

const DashboardProject = ({ project, refreshProjects, projectData, handleStopProject }: DashboardProjectProps) => {
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;
    const {
        globalState: { userData }
    } = useGlobalStore();

    const [sectionData, setSectionData] = React.useState<Partial<SectionRuntime>>({});
    const [jumpWorking, setJumpWorking] = React.useState(false);
    type CircadianChartPoint = {
        time: number;
        value: number;
        tooltip: { time: string; spectrumMixName: string; lightRecipeName: string };
        color?: { uv: number; blue: number; green: number; red: number; fr: number; ir: number };
    };
    const [chartData, setChartData] = React.useState<{ pfd: CircadianChartPoint[]; irradiance: CircadianChartPoint[] }>({ pfd: [], irradiance: [] });
    const [spectrumChartData, setSpectrumChartData] = React.useState<
        Partial<{
            chart: ReturnType<typeof aggregateSpectrograms>;
            maxIntensity: number;
            maxIrradianceIntensity: number;
            channelIrradiance: Record<string, number>;
        }>
    >({});
    const [projectStatus, setProjectStatus] = React.useState<Partial<ProjectStatus>>({});
    const [projectChannelPercents, setProjectChannelPercents] = React.useState('');
    const [currentDli, setCurrentDli] = React.useState<number | null>(null);
    const [sectionDlis, setSectionDlis] = React.useState<SectionDliInfo[] | null>(null);
    const [sectionDlisLoading, setSectionDlisLoading] = React.useState(false);
    const [mirrorChannelMap, setMirrorChannelMap] = React.useState<Record<string, { name: string; dmxAddress: number }[]>>({});

    React.useEffect(() => {
        if (!project.running || (project.linkCount ?? 0) === 0) {
            return;
        }
        lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${project.zoneId}`).then((data) => {
            if (!data) {
                return;
            }
            const mirrorLinks = data.filter((l) => l.type === 'mirror' && l.enabled);
            const map: Record<string, { name: string; dmxAddress: number }[]> = {};
            for (const link of mirrorLinks) {
                for (const entry of link.channelMap) {
                    if (!map[entry.sourceChannelId]) {
                        map[entry.sourceChannelId] = [];
                    }
                    map[entry.sourceChannelId].push({ name: link.name, dmxAddress: entry.dmxAddress });
                }
            }
            setMirrorChannelMap(map);
        });
    }, [project.id, project.running, project.linkCount, project.zoneId]);

    React.useEffect(() => {
        if (!project.running) {
            setProjectStatus((prev) => ({ ...prev, section: undefined }));
        }
    }, [project.running]);

    React.useEffect(() => {
        if (!projectData) {
            return;
        }
        setProjectStatus(projectData.status);

        const percents = (projectData.status.channels ?? []).map((e) => e.percent).join('-');
        setProjectChannelPercents((prev) => (prev === percents ? prev : percents));
    }, [projectData]);

    React.useEffect(() => {
        (async () => {
            if (!projectStatus) {
                return;
            }

            const waveLengthIntensities: number[] = [];
            const waveLengthIrradianceIntensities: number[] = [];
            if (projectStatus.section?.id !== sectionData.id && project.recipe?.sectionList) {
                const spectrumMixCache: Record<string, SpectrumMixData> = {};
                const found = project.recipe.sectionList.find((e) => e.id === projectStatus.section?.id) as SectionRuntime | undefined;
                if (!found) {
                    return;
                }
                const section: SectionRuntime = { ...found, circadianEvents: [...found.circadianEvents], lightChannels: {} };

                for (const eventIndex in section.circadianEvents) {
                    const event = section.circadianEvents[eventIndex];
                    const isDark = !event.lightRecipeId || event.lightRecipeId === DARK_LIGHT_RECIPE_ID;
                    const spectrumMixId = isDark
                        ? null
                        : (project.spectrogramMap.find((item) => item.lightRecipeId === event.lightRecipeId)?.spectrumMixId ?? null);

                    if (spectrumMixId) {
                        if (!spectrumMixCache[spectrumMixId]) {
                            spectrumMixCache[spectrumMixId] = (await lediumApi.get<SpectrumMixData>(
                                `spectrumMixes/withLightChannels/${spectrumMixId}`
                            )) as SpectrumMixData;
                        }

                        section.circadianEvents[eventIndex] = {
                            ...event,
                            spectrumMix: spectrumMixCache[spectrumMixId]
                        };

                        spectrumMixCache[spectrumMixId].lightChannels.forEach((lightChannel) => {
                            if (!section.lightChannels[lightChannel.id]) {
                                section.lightChannels[lightChannel.id] = {
                                    id: lightChannel.id,
                                    name: lightChannel.name,
                                    spectrogram: lightChannel.spectrogram,
                                    spectrogramRanges: lightChannel.spectrogramRanges
                                };
                            }
                        });

                        // max wavelength intensity
                        const aggregateMix = aggregateSpectrograms(spectrumMixCache[spectrumMixId].lightChannels);
                        waveLengthIntensities.push(Math.max(...aggregateMix.map((e) => e.pfd)));
                        waveLengthIrradianceIntensities.push(Math.max(...aggregateMix.map((e) => e.irradiance)));
                    } else {
                        section.circadianEvents[eventIndex] = {
                            ...event,
                            spectrumMix: { id: '', name: '', lightRecipe: { name: '' }, lightChannels: [] }
                        };
                    }
                }
                section.maxWaveLengthIntensity = Math.round(Math.max(...waveLengthIntensities) * 10) / 10;
                section.maxIrradianceIntensity = Math.round(Math.max(...waveLengthIrradianceIntensities) * 10) / 10;
                setSectionData(section);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectStatus?.section?.id]);

    React.useEffect(() => {
        if (!sectionData.circadianEvents) {
            return;
        }

        type TooltipData = { time: string; spectrumMixName: string; lightRecipeName: string };
        type ColorData = { uv: number; blue: number; green: number; red: number; fr: number; ir: number };
        let nextTurnOff1BitPfd = 0;
        let nextTurnOff1BitIrradiance = 0;
        let tooltip: TooltipData = { time: '', spectrumMixName: '', lightRecipeName: '' };
        let eventColor: ColorData = { uv: 0, blue: 0, green: 0, red: 0, fr: 0, ir: 0 };
        const pfdChart: CircadianChartPoint[] = [];
        const irradianceChart: CircadianChartPoint[] = [];

        [...sectionData.circadianEvents]
            .sort((a, b) => (+a.hour + a.minute / 60 > +b.hour + b.minute / 60 ? 1 : -1))
            .forEach((event) => {
                const time = event.hour + event.minute / 60;
                const [fadePfd, turnPfd, fadeIrradiance, turnIrradiance, uv, blue, green, red, fr, ir] = event.spectrumMix.lightChannels.reduce(
                    (a: [number, number, number, number, number, number, number, number, number, number], lightChannel) => {
                        if (lightChannel.resolution === 1) {
                            a[1] += (lightChannel.intensityRanges.pfd.sum / 100) * lightChannel.intensityPercent;
                            a[3] += (lightChannel.intensityRanges.irradiance.sum / 100) * lightChannel.intensityPercent;
                        } else {
                            a[0] += (lightChannel.intensityRanges.pfd.sum / 100) * lightChannel.intensityPercent;
                            a[2] += (lightChannel.intensityRanges.irradiance.sum / 100) * lightChannel.intensityPercent;
                        }
                        a[4] += (lightChannel.intensityRanges.pfd.uv / 100) * lightChannel.intensityPercent;
                        a[5] += (lightChannel.intensityRanges.pfd.blue / 100) * lightChannel.intensityPercent;
                        a[6] += (lightChannel.intensityRanges.pfd.green / 100) * lightChannel.intensityPercent;
                        a[7] += (lightChannel.intensityRanges.pfd.red / 100) * lightChannel.intensityPercent;
                        a[8] += (lightChannel.intensityRanges.pfd.fr / 100) * lightChannel.intensityPercent;
                        a[9] += (lightChannel.intensityRanges.pfd.ir / 100) * lightChannel.intensityPercent;
                        return a;
                    },
                    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                );

                const isDarkEvent = !event.lightRecipeId || event.lightRecipeId === DARK_LIGHT_RECIPE_ID;
                tooltip = {
                    spectrumMixName: isDarkEvent ? '' : event.spectrumMix.name,
                    lightRecipeName: isDarkEvent ? (i18n('dark_light_recipe_name') as string) : event.spectrumMix.lightRecipe.name,
                    time: `${('0' + event.hour).slice(-2)}:${('0' + event.minute).slice(-2)}`
                };
                eventColor = { uv, blue, green, red, fr, ir };

                pfdChart.push({ time, value: nextTurnOff1BitPfd + fadePfd, tooltip, color: eventColor });
                irradianceChart.push({ time, value: nextTurnOff1BitIrradiance + fadeIrradiance, tooltip, color: eventColor });

                if (turnPfd) {
                    nextTurnOff1BitPfd = turnPfd;
                    pfdChart.push({ time: time + 1 / 3600, value: fadePfd + turnPfd, tooltip, color: eventColor });
                } else {
                    nextTurnOff1BitPfd = 0;
                }

                if (turnIrradiance) {
                    nextTurnOff1BitIrradiance = turnIrradiance;
                    irradianceChart.push({ time: time + 1 / 3600, value: fadeIrradiance + turnIrradiance, tooltip, color: eventColor });
                } else {
                    nextTurnOff1BitIrradiance = 0;
                }
            });

        if (nextTurnOff1BitPfd) {
            pfdChart.push({ time: 23 + 55 / 60 - 1 / 3600, value: nextTurnOff1BitPfd, tooltip, color: eventColor });
        }
        if (nextTurnOff1BitIrradiance) {
            irradianceChart.push({ time: 23 + 55 / 60 - 1 / 3600, value: nextTurnOff1BitIrradiance, tooltip, color: eventColor });
        }

        setChartData({ pfd: pfdChart, irradiance: irradianceChart });
    }, [sectionData]);

    React.useEffect(() => {
        if (!projectStatus?.channels || !projectChannelPercents || !sectionData.lightChannels) {
            return;
        }

        const activeChannels = projectStatus.channels
            .filter((channel) => channel.percent > 0)
            .map((channel) => ({
                ...channel,
                intensityPercent: channel.percent,
                spectrogram: sectionData.lightChannels![channel.id]?.spectrogram
            }));

        if (activeChannels.some((channel) => !channel.spectrogram)) {
            return;
        }

        const spectrumChart = aggregateSpectrograms(activeChannels as Array<{ spectrogram: SpectrogramPoint[]; intensityPercent: number }>);

        const channelIrradiance: Record<string, number> = {};
        for (const channel of projectStatus.channels) {
            const lightChannel = sectionData.lightChannels![channel.id];
            if (lightChannel) {
                if (channel.resolution > 1) {
                    channelIrradiance[channel.id] = Math.round((lightChannel.spectrogramRanges.irradiance.sum / 100) * channel.percent);
                } else {
                    channelIrradiance[channel.id] = channel.value ? lightChannel.spectrogramRanges.irradiance.sum : 0;
                }
            }
        }

        setSpectrumChartData({
            chart: spectrumChart,
            maxIntensity: sectionData.maxWaveLengthIntensity,
            maxIrradianceIntensity: sectionData.maxIrradianceIntensity,
            channelIrradiance
        });
    }, [projectChannelPercents, projectStatus?.channels, sectionData.lightChannels, sectionData.maxIrradianceIntensity, sectionData.maxWaveLengthIntensity]);

    React.useEffect(() => {
        setCurrentDli(chartData.pfd.length >= 2 ? calculateDli(chartData.pfd) : null);
    }, [chartData.pfd]);

    async function computeAllSectionDlis() {
        if (!project.recipe?.sectionList || sectionDlisLoading || sectionDlis) {
            return;
        }
        setSectionDlisLoading(true);

        const spectrumMixCache: Record<string, SpectrumMixData> = {};
        const results: SectionDliInfo[] = [];

        for (const section of project.recipe.sectionList) {
            const events: CircadianEventRuntime[] = [];

            for (const event of section.circadianEvents) {
                const mapItem = project.spectrogramMap.find((item) => item.lightRecipeId === event.lightRecipeId);
                if (mapItem?.spectrumMixId) {
                    const spectrumMix = await fetchSpectrumMix(mapItem.spectrumMixId, spectrumMixCache);
                    if (spectrumMix) {
                        events.push({ ...event, spectrumMix });
                    }
                }
            }

            const pfdPoints = buildSectionPfdPoints(events);
            results.push({
                id: section.id,
                name: section.name,
                repeatNum: section.repeatNum,
                dliPerDay: calculateDli(pfdPoints)
            });
        }

        setSectionDlis(results);
        setSectionDlisLoading(false);
    }

    async function startProject(startTime: number | null) {
        const result = await lediumApi.get(`pRunner/startProject/${project.id}${startTime ? '/' + startTime : ''}`);
        if (!result) {
            handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
            return;
        }
        refreshProjects();
        handleSnackbar({
            message: i18n('project_started') as string,
            severity: 'success'
        });
    }

    async function stopProject() {
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_stop_the_project')}</span>,
            destructive: true,
            okCallback: async () => {
                const result = await lediumApi.get(`pRunner/stopProject/${project.id}`);
                if (!result) {
                    handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
                    return;
                }
                handleStopProject(project.id);
                handleSnackbar({
                    message: i18n('project_stopped') as string,
                    severity: 'warning'
                });
            }
        });
    }

    async function pauseProject() {
        if (project.pauseData) {
            const result = await lediumApi.get(`pRunner/resumeProject/${project.id}`);
            if (!result) {
                handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
                return;
            }
            handleSnackbar({
                message: i18n('project_resumed') as string
            });
        } else {
            const result = await lediumApi.get(`pRunner/pauseProject/${project.id}`);
            if (!result) {
                handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
                return;
            }
            handleSnackbar({
                message: i18n('project_paused') as string,
                severity: 'info'
            });
        }
        refreshProjects();
    }

    async function jumpSection(direction: 'back' | 'restart' | 'forward') {
        if (!['back', 'restart', 'forward'].includes(direction)) {
            return;
        }
        setJumpWorking(true);
        const result = await lediumApi.get(`pRunner/jumpSection/${direction}/${project.id}`);
        setJumpWorking(false);
        if (!result) {
            handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
            return;
        }
        handleSnackbar({ message: i18n('section_jumped') as string, severity: 'info' });
    }
    return (
        <DashboardProjectView
            project={project}
            projectStatus={projectStatus}
            chartData={chartData}
            spectrumChartData={spectrumChartData}
            startProject={startProject}
            stopProject={stopProject}
            pauseProject={pauseProject}
            userData={userData}
            jumpSection={jumpSection}
            jumpWorking={jumpWorking}
            currentDli={currentDli}
            sectionDlis={sectionDlis}
            sectionDlisLoading={sectionDlisLoading}
            onRequestSectionDlis={computeAllSectionDlis}
            mirrorChannelMap={mirrorChannelMap}
        />
    );
};

export default React.memo(DashboardProject);
